import { AccountType, DocumentType, Employee, Position, Tag } from '@synergy/models';
import { Box, Stack, Theme, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { Localized, PoolButton, PoolChip, PoolModal, ValueOrNDash } from '@synergy/components';
import { LockIcon, PlusCircleIcon } from '@synergy/theme';
import { PaloColumn, PaloListView, PaloLockedSwitch, PaloRolePills, PaloRowAction } from '@synergy/components-palo';
import { PoolFilterSearchBar, PoolLink, PoolTypography } from '@synergy/components-next';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import * as ActivityActions from '../../../../../common/actions/activity-actions';
import * as SupplierEmployeeActions from './store/supplier-employee-actions';
import { AddEmployeeModal } from '../../../components/employee/add-employee-modal';
import { ChangeEmployeeTagsModal } from '../../../components/employee/change-employee-tags-modal';
import { ChangePositionModal } from '../../../components/employee/change-position-modal';
import { EmployeeRoles } from './components/employee-roles';
import { ROUTES } from '../../../../sidebar-menu/navigation-drawer/routes';
import { RemoveEmployeeModal } from './components/remove-employee-modal';
import { bySupplierEmployeeName, supplierEmployeeName } from '../../../utils/employee-name';
import { filterEmployees } from '../../../utils/filter-employees';
import { supplierEmployeeSelector, supplierHasBonusConditionErrorSelector } from './store/supplier-employee-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    table: {
        backgroundColor: 'white',
        '& tbody tr:last-child td': {
            paddingRight: 24,
        },
        marginBottom: theme.spacing(2),
    },
    mainContact: {
        color: theme.palette.primary.main,
    },
    position: {
        color: theme.palette.grey[700],
    },
    chip: {
        marginLeft: 0,
        marginRight: theme.spacing(1),
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
        backgroundColor: '#F2F2F2',
    },
    dash: {
        marginLeft: 8,
        color: theme.palette.grey[700],
    },
    addButton: {
        fontSize: 20,
    },
    disabledRow: {
        opacity: 0.5,
    },
}));

export const SupplierEmployees = () => {
    const classes = useStyles();
    const { id: supplierIdParam } = useParams<{ id: string }>();
    const [removeModalData, setRemoveModalData] = useState<Employee | null>(null);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [changePositionModalData, setChangePositionModalData] = useState<Employee | null>(null);
    const [tagsModalData, setTagsModalData] = useState<Employee | null>(null);
    const navigate = useNavigate();
    const theme = useTheme();
    const locales = useAppSelector(state => state.supportedLocales);
    const intl = useIntl();
    const hasBonusConditionError = useAppSelector(supplierHasBonusConditionErrorSelector);

    const supplierEmployees = useAppSelector(supplierEmployeeSelector);
    const dispatch = useThunkDispatch();

    const [employees, setEmployees] = useState<Employee[] | null>(supplierEmployees);
    const [search, setSearch] = useState('');
    /*
     * Someone who has left is out of the way until asked for, as in every
     * other master data list (LST-S14; the tab was left to follow with
     * #2221).
     */
    const [showLocked, setShowLocked] = useState(false);

    useEffect(() => {
        if (supplierIdParam && +supplierIdParam) {
            dispatch(SupplierEmployeeActions.fetchSupplierEmployees(+supplierIdParam));
        }
    }, [supplierIdParam, dispatch]);

    useEffect(() => {
        setEmployees(filterEmployees(supplierEmployees, search, locales));
    }, [supplierEmployees, search, locales]);

    if (!employees) {
        return null;
    }

    const handleRemove = async (
        reassignMainContactId?: number,
        reassignBonusContactId?: number,
        reassignImportContactId?: number
    ) => {
        if (removeModalData?.id && supplierIdParam) {
            await dispatch(
                SupplierEmployeeActions.removeEmployee(
                    +supplierIdParam,
                    removeModalData?.id,
                    reassignMainContactId,
                    reassignBonusContactId,
                    reassignImportContactId
                )
            );
            dispatch(ActivityActions.fetchActivities(DocumentType.Supplier, +supplierIdParam));
            setRemoveModalData(null);
        }
    };

    const handleAdd = async (newEmployee: Employee) => {
        if (supplierIdParam) {
            await dispatch(
                SupplierEmployeeActions.addEmployee(+supplierIdParam, {
                    ...newEmployee,
                    isMainContact: !employees.some(employee => employee.isMainContact),
                })
            );
            dispatch(ActivityActions.fetchActivities(DocumentType.Supplier, +supplierIdParam));
        }
        setShowAddModal(false);
    };

    const handleSetMainContact = (employeeId: number) => {
        if (supplierIdParam) {
            dispatch(SupplierEmployeeActions.setEmployeeAsMainContact(+supplierIdParam, employeeId));
        }
    };

    const handleEdit = (employeeId: number) => {
        navigate(ROUTES.contactsAndOrganizations.contacts.detail.replace(':id', employeeId.toString()));
    };

    const handleChangePosition = (employee: Employee | null, position: Position | null) => {
        if (employee && supplierIdParam) {
            dispatch(SupplierEmployeeActions.changeEmployeePosition(+supplierIdParam, employee.id, position));
        }
        setChangePositionModalData(null);
    };

    const handleChangeTags = (employee: Employee | null, tags: Tag[]) => {
        if (employee && supplierIdParam) {
            dispatch(SupplierEmployeeActions.changeEmployeeTags(+supplierIdParam, employee.id, tags));
        }
        setTagsModalData(null);
    };

    // The same order the row menu had. A locked person keeps only the entries
    // that still mean something.
    const employeeActions = (employee: Employee): PaloRowAction[] => [
        ...(employee.isLocked || employee.isMainContact
            ? []
            : [
                  {
                      label: <FormattedMessage id="app.contactsAndOrganizations.detail.mainContact" />,
                      onClick: () => handleSetMainContact(employee.id),
                  },
              ]),
        ...(employee.isLocked
            ? []
            : [
                  {
                      label: employee.position ? (
                          <FormattedMessage id="app.contactsAndOrganizations.detail.changePosition" />
                      ) : (
                          <FormattedMessage id="app.contactsAndOrganizations.detail.addPosition" />
                      ),
                      onClick: () => setChangePositionModalData(employee),
                  },
              ]),
        {
            label: <FormattedMessage id="app.contactsAndOrganizations.detail.editPerson" />,
            onClick: () => handleEdit(employee.id),
        },
        {
            label: <FormattedMessage id="app.contactsAndOrganizations.detail.assignTags" />,
            onClick: () => setTagsModalData(employee),
        },
        {
            label: <FormattedMessage id="app.contactsAndOrganizations.detail.removePerson" />,
            onClick: () => setRemoveModalData(employee),
        },
    ];

    const columns: PaloColumn<Employee>[] = [
        {
            key: 'name',
            type: 'name',
            head: <FormattedMessage id="app.table.column.name" />,
            cell: employee => (
                <Stack direction="row" spacing={1} alignItems="center">
                    {employee.isLocked && <LockIcon style={{ fontSize: 16 }} />}
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{supplierEmployeeName(employee)}</Box>
                    <PaloRolePills
                        roles={[
                            employee.isMainContact && (
                                <FormattedMessage id="app.contactsAndOrganizations.detail.mainContact" />
                            ),
                            employee.hasBonusConditions && (
                                <FormattedMessage id="app.contactsAndOrganizations.detail.bonusContact" />
                            ),
                            employee.isImportContact && (
                                <FormattedMessage id="app.contactsAndOrganizations.detail.importContact" />
                            ),
                        ]}
                    />
                </Stack>
            ),
        },
        {
            // The job title is not one of the functions above: it says what
            // the person does at their own house, so it gets a column of its
            // own rather than a second line (LST-S3).
            key: 'position',
            type: 'classification',
            head: <FormattedMessage id="app.label.position" />,
            cell: employee => (
                <ValueOrNDash>
                    <Localized value={employee.position?.name} />
                </ValueOrNDash>
            ),
        },
        {
            key: 'email',
            type: 'name',
            variant: 'muted',
            head: <FormattedMessage id="app.common.email" />,
            cell: employee => (
                <PoolLink
                    href={`mailto:${employee.email}`}
                    colorVariant="grey700"
                    hoverColor={theme.palette.primary.main}
                    onClick={e => e.stopPropagation()}
                >
                    {employee.email}
                </PoolLink>
            ),
        },
        {
            // Mobile if there is one, else the main number, picked by the
            // server; a click calls rather than opening the row (#2315).
            key: 'phone',
            type: 'phone',
            variant: 'muted',
            head: <FormattedMessage id="app.common.phone" />,
            cell: employee => (
                <ValueOrNDash>
                    {employee.phone && (
                        <PoolLink
                            href={`tel:${employee.phone.replace(/[^\d+]/g, '')}`}
                            colorVariant="grey700"
                            hoverColor={theme.palette.primary.main}
                            onClick={e => e.stopPropagation()}
                        >
                            {employee.phone}
                        </PoolLink>
                    )}
                </ValueOrNDash>
            ),
        },
        {
            key: 'roles',
            wrap: true,
            head: <FormattedMessage id="app.contactsAndOrganizations.detail.roles" />,
            cell: employee => <EmployeeRoles employee={employee} />,
        },
        {
            key: 'tags',
            wrap: true,
            head: <FormattedMessage id="app.contactsAndOrganizations.detail.tags" />,
            cell: employee => (
                <ValueOrNDash dashProps={{ className: classes.dash }}>
                    {employee.tags?.map(tag => (
                        <PoolChip key={tag.id} className={classes.chip} label={tag.name} type="tag" />
                    ))}
                </ValueOrNDash>
            ),
        },
    ];

    /*
     * Someone who has left is still on the list, only out of play. Above them
     * all stands the main contact: this table has no sorting of its own, so
     * the order it opens in is the only one there is (LST-S3a).
     */
    const sorted = [...employees]
        .filter(employee => showLocked || !employee.isLocked)
        .sort((a, b) => {
            if (a.isLocked !== b.isLocked) {
                return a.isLocked ? 1 : -1;
            }

            if (a.isMainContact !== b.isMainContact) {
                return a.isMainContact ? -1 : 1;
            }

            return bySupplierEmployeeName(a, b);
        });

    return (
        <>
            <Stack
                justifyContent="flex-end"
                alignItems="center"
                gap={2}
                flexDirection="row"
                sx={{ margin: theme.spacing(2, theme.general.pageContentSpacing, 0, theme.general.pageContentSpacing) }}
            >
                <PaloLockedSwitch checked={showLocked} onChange={setShowLocked} />
                <PoolFilterSearchBar
                    onChange={setSearch}
                    placeholder={intl.formatMessage({
                        id: 'app.common.searchPlaceholder',
                    })}
                    onKeyDown={e => e.stopPropagation()}
                    value={search}
                    sx={{ minWidth: 230, width: 230 }}
                    backgroundColor="transparent"
                />
            </Stack>

            <Box
                sx={{
                    margin: theme.spacing(2, theme.general.pageContentSpacing),
                }}
            >
                {sorted.length > 0 && (
                    <PaloListView<Employee, number>
                        embedded={true}
                        columns={columns}
                        items={sorted}
                        getRowId={employee => employee.id}
                        emptyLabel={<FormattedMessage id="app.contactsAndOrganizations.detail.addPerson" />}
                        isRowMuted={employee => employee.isLocked}
                        onRowClick={employee => handleEdit(employee.id)}
                        rowHref={employee =>
                            ROUTES.contactsAndOrganizations.contacts.detail.replace(':id', employee.id.toString())
                        }
                        rowActions={employee => employeeActions(employee)}
                    />
                )}

                <PoolButton
                    styleType="thirdLevel"
                    startIcon={<PlusCircleIcon color="primary" className={classes.addButton} />}
                    onClick={() => setShowAddModal(true)}
                >
                    <FormattedMessage id="app.contactsAndOrganizations.detail.addPerson" />
                </PoolButton>

                <AddEmployeeModal
                    isVisible={showAddModal}
                    organizationId={+(supplierIdParam || 0)}
                    employeeType={AccountType.Supplier}
                    onCancel={() => setShowAddModal(false)}
                    onConfirm={handleAdd}
                />
                <ChangePositionModal
                    employeeType={AccountType.Supplier}
                    isVisible={!!changePositionModalData}
                    position={changePositionModalData?.position}
                    onCancel={() => setChangePositionModalData(null)}
                    onConfirm={position => handleChangePosition(changePositionModalData, position)}
                />
                <ChangeEmployeeTagsModal
                    isVisible={!!tagsModalData}
                    tags={tagsModalData?.tags}
                    onCancel={() => setTagsModalData(null)}
                    onConfirm={tags => handleChangeTags(tagsModalData, tags)}
                />
                <RemoveEmployeeModal
                    isVisible={!!removeModalData}
                    employees={employees}
                    employee={removeModalData}
                    onCancel={() => setRemoveModalData(null)}
                    onConfirm={handleRemove}
                />

                <PoolModal
                    isVisible={hasBonusConditionError}
                    title={<FormattedMessage id="app.contactsAndOrganizations.contact.conditionError" />}
                    handleClose={() => dispatch(SupplierEmployeeActions.resetBonusConditionError())}
                    sx={{ content: { textAlign: 'center' } }}
                    actionChildren={[
                        <span key="1" />,
                        <PoolButton
                            key="2"
                            styleType="firstLevel"
                            onClick={() => dispatch(SupplierEmployeeActions.resetBonusConditionError())}
                        >
                            <FormattedMessage id="app.common.okAgreed" />
                        </PoolButton>,
                    ]}
                >
                    <FormattedMessage id="app.contactsAndOrganizations.contact.bonusConditionErrorMessage" />
                </PoolModal>
            </Box>
        </>
    );
};
